import { Grid } from "@mui/material"
import { getAdvertiserNameById } from "advertiser/tools/common"
import { translate } from "app/language/service"
import { getConvertedTemperature, getTemperatureUnitSymbol } from "app/tools/temperature"
import { isCampaignEditable } from "campaign/tools/edit"
import { getDspNameById } from "dsp/tools/common"
import { FORM_TABS, FORM_TAB_BOXES, ISummaryPageInfo } from "form/model"
import { editFormFromSummaryView } from "form/tools/edit"
import { getSummaryOperatorsWithUmbracityIfNeeded } from "form/tools/ssp-operator-umbracity"
import { getOrderNameById } from "order/tools/common"
import React from "react"
import { FlexBox } from "ui/components/FlexBox"
import { TestHiddenData } from "ui/components/TestHiddenData"
import { getTagNamesByIds } from "user-tag/tools/common"
import { canSaveCampaigns } from "user/tools/permissions"
import { CampaignAudienceSummary } from "./CampaignAudienceSummary"
import { CampaignCapsSummary } from "./CampaignCapsSummary"
import { CampaignCreativeSummary } from "./CampaignCreativeSummary"
import { CampaignEnvironmentsSummary } from "./CampaignEnvironmentsSummary"
import { CampaignGeoTargetsSummary } from "./CampaignGeoTargetsSummary"
import { CampaignOperatorsSummary } from "./CampaignOperatorsSummary"
import { CampaignScheduleSummary } from "./CampaignScheduleSummary"
import { CampaignSspDealsSummary } from "./CampaignSspDealsSummary"
import { CampaignSspSummary } from "./CampaignSspSummary"
import { CampaignSummaryColumn } from "./CampaignSummaryColumn"
import { CampaignSummaryItem } from "./CampaignSummaryItem"
import { CampaignSummaryItemLayout } from "./CampaignSummaryItemLayout"
import { CampaignWeatherSummary } from "./CampaignWeatherSummary"
import { DspDealAdvertiserDomainsSummary } from "./DspDealAdvertiserDomainsSummary"
import { DspDealFormatSummary } from "./DspDealFormatSummary"
import { ForecastRequiredCreativeSummary } from "./ForecastRequiredCreativeSummary"

export const SummaryParameters = React.memo(
  ({ form, contentWidth: cw, isForecast, isDspDeal }: ISummaryPageInfo): JSX.Element => {
    const isEditable = canSaveCampaigns() && isCampaignEditable(form.statusId)
    const tagNames = isForecast ? getTagNamesByIds(form.configuration.main.userTagIds) : []
    return (
      <FlexBox
        id="campaign-summary-content"
        vertical
        ai="start"
        jc="start"
        sx={{
          width: "100%",
          p: "1rem",
          pb: "1.5rem",
          mt: "1.5rem",
          mb: "3rem",
          animation: "fadeIn 600ms",
          flexGrow: "1",
          overflow: "auto",
          backgroundColor: theme => theme.palette.background.paper
        }}>
        <TestHiddenData id="campaign-id" data={form.campaignId} />
        <Grid container spacing={2}>
          <CampaignSummaryItemLayout
            // ****************** MAIN ******************
            id="main"
            title={translate(
              isForecast
                ? "campaign.configuration.main.forecast.title"
                : isDspDeal
                  ? "dsp-deal.configuration.dspDeal.title"
                  : "campaign.configuration.main.campaign.title"
            )}
            isEditable={isEditable}
            onEdit={() =>
              editFormFromSummaryView(FORM_TABS.CONFIGURATION, isForecast, isDspDeal, FORM_TAB_BOXES.CONFIGURATION_MAIN)
            }
            xl={6}
            sxContent={{ gap: 0 }}>
            <CampaignSummaryColumn contentWidth={cw} twoColumns>
              <CampaignSummaryItem
                id="summary-name"
                title={translate(isForecast ? "forecast.name" : isDspDeal ? "dsp-deal.name.title" : "campaign.name")}
                value={form.configuration.main.name}
              />
              {!isForecast && (
                <>
                  <CampaignSummaryItem
                    id="summary-order"
                    title={translate("label.order")}
                    value={getOrderNameById(form.configuration.main.order_id as number)}
                  />
                  {!isDspDeal && (
                    <CampaignSummaryItem
                      id="summary-advertiser"
                      title={translate("label.advertiser")}
                      value={getAdvertiserNameById(form.configuration.main.advertiser_id as number)}
                    />
                  )}
                </>
              )}
              <CampaignSummaryItem
                id="summary-impressions"
                title={translate("label.impressions")}
                value={
                  form.configuration.main.unlimitedImpressions
                    ? translate("label.impressions.unlimited")
                    : form.configuration.main.max_views
                }
              />
              <CampaignSummaryItem
                id="summary-language"
                title={translate("label.language")}
                value={translate(`label.language.${form.configuration.main.language_id}`)}
              />
              <CampaignSummaryItem
                id="summary-start-date"
                title={translate("label.startDate")}
                value={form.configuration.main.startDate}
              />
              <CampaignSummaryItem
                id="summary-end-date"
                title={translate("label.endDate")}
                value={form.configuration.main.endDate}
              />
              {isForecast && (
                <>
                  <CampaignSummaryItem
                    id="summary-tag"
                    title={translate("label.tag.title")}
                    tooltip={tagNames.length < 5 ? undefined : tagNames.join(", ")}
                    value={
                      tagNames.length < 5
                        ? tagNames.join(", ")
                        : `${tagNames.slice(0, 4).join(", ")}, ...+${tagNames.length - 4}`
                    }
                  />
                  {form.configuration.main.description && (
                    <CampaignSummaryItem
                      id="summary-description"
                      title={translate("label.description")}
                      value={form.configuration.main.description}
                      textarea
                    />
                  )}
                </>
              )}
              {isDspDeal && (
                <>
                  <CampaignSummaryItem
                    id="summary-dsp-name"
                    title="DSP"
                    value={getDspNameById(form.configuration.dspDeal.dsp_id)}
                  />
                  {form.configuration.dspDeal.description && (
                    <CampaignSummaryItem
                      id="summary-dsp-description"
                      title={translate("label.description")}
                      value={form.configuration.dspDeal.description}
                      textarea
                    />
                  )}
                </>
              )}
            </CampaignSummaryColumn>
            {isDspDeal && (
              <CampaignSummaryColumn contentWidth={cw} sx={{ pt: 0 }}>
                <CampaignSummaryItem id="summary-deal-id" title="Dealid" value={form.configuration.dspDeal.dealid} />
                <CampaignSummaryItem
                  id="summary-advertiser-domains"
                  title={translate("label.advertiser-domain")}
                  sxChildren={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "start",
                    gap: "1rem",
                    ml: "1rem",
                    mt: ".5rem",
                    maxHeight: "10rem",
                    overflow: "auto"
                  }}>
                  <DspDealAdvertiserDomainsSummary
                    advertiserDomains={form.context.relations.dspDealAdvertiserDomains}
                  />
                </CampaignSummaryItem>
              </CampaignSummaryColumn>
            )}
          </CampaignSummaryItemLayout>
          <CampaignSummaryItemLayout
            // ****************** BUDGET ******************
            id="budget"
            title={translate("campaign.configuration.budget.title")}
            isEditable={isEditable}
            onEdit={() =>
              editFormFromSummaryView(
                FORM_TABS.CONFIGURATION,
                isForecast,
                isDspDeal,
                FORM_TAB_BOXES.CONFIGURATION_BUDGET
              )
            }
            xl={6}>
            <CampaignSummaryColumn contentWidth={cw}>
              <CampaignSummaryItem
                id="summary-currency"
                title={translate("label.currency")}
                value={form.configuration.budget.currency_id}
              />
              {!isDspDeal && (
                <>
                  <CampaignSummaryItem
                    id="summary-campaign-budget"
                    title={translate("label.budget")}
                    value={form.configuration.budget.budget}
                  />
                  <CampaignSummaryItem
                    id="summary-max-spend"
                    title={translate("label.daily.max.spend")}
                    value={form.configuration.budget.max_daily_spend}
                  />
                  <CampaignSummaryItem
                    id="summary-budget-daily-equalized"
                    title={translate("campaign.budget.daily.equilized")}
                    value={translate(
                      form.configuration.budget.equalizeDailyMaxSpendFromSchedule ? "active.true" : "active.false"
                    )}
                  />
                  <CampaignSummaryItem
                    id="summary-max-bid"
                    title={translate("label.max.bid")}
                    value={form.configuration.budget.max_bid}
                  />
                </>
              )}
              {!isDspDeal ? (
                <CampaignSummaryItem
                  id="summary-auction"
                  title={translate("label.auctions")}
                  value={
                    form.configuration.budget.auctions.length === 0
                      ? null
                      : form.configuration.budget.auctions.map(a => translate(`label.auction.${a}`)).join(",")
                  }
                />
              ) : (
                <>
                  <CampaignSummaryItem
                    id="summary-auction-type"
                    title={translate("label.auction")}
                    value={translate(`label.auction.${form.configuration.dspDeal.auction_type}`)}
                  />
                  <CampaignSummaryItem
                    id="summary-bid-floor"
                    title={translate("label.bid-floor")}
                    value={form.configuration.dspDeal.bid_floor || ""}
                  />
                </>
              )}
            </CampaignSummaryColumn>
          </CampaignSummaryItemLayout>
          {!isDspDeal && (
            <>
              <CampaignSummaryItemLayout
                // ****************** CAPS ******************
                id="caps"
                title={translate("campaign.configuration.caps.title")}
                isEditable={isEditable}
                onEdit={() =>
                  editFormFromSummaryView(
                    FORM_TABS.CONFIGURATION,
                    isForecast,
                    isDspDeal,
                    FORM_TAB_BOXES.CONFIGURATION_CAPS
                  )
                }
                xl={6}>
                <CampaignCapsSummary contentWidth={cw} caps={form.configuration.caps} />
              </CampaignSummaryItemLayout>
              <CampaignSummaryItemLayout
                // ****************** AUDIENCE ******************
                id="audience"
                title={translate("audience.title")}
                isEditable={isEditable}
                onEdit={() => editFormFromSummaryView(FORM_TABS.AUDIENCE, isForecast, isDspDeal)}
                xl={6}
                md={6}>
                <CampaignSummaryColumn contentWidth={cw}>
                  <CampaignSummaryItem
                    id="summary-audiences"
                    title={translate("label.included")}
                    sxChildren={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      justifyContent: "start",
                      gap: "1rem",
                      ml: "1rem",
                      mt: ".5rem",
                      maxHeight: "10rem",
                      overflow: "auto"
                    }}>
                    <CampaignAudienceSummary audienceIds={form.audience.selectedIds} />
                  </CampaignSummaryItem>
                </CampaignSummaryColumn>
              </CampaignSummaryItemLayout>
            </>
          )}
          <CampaignSummaryItemLayout
            // ****************** SCHEDULE ******************
            id="schedule"
            title={translate("campaign.configuration.schedule.title")}
            isEditable={isEditable}
            onEdit={() =>
              editFormFromSummaryView(
                FORM_TABS.CONFIGURATION,
                isForecast,
                isDspDeal,
                FORM_TAB_BOXES.CONFIGURATION_SCHEDULE
              )
            }
            md={12}
            xl={12}>
            <CampaignSummaryColumn contentWidth={cw}>
              <CampaignSummaryItem
                id="summary-schedule"
                title={translate("label.schedule.title")}
                sxChildren={{ display: "flex", flexWrap: "wrap" }}>
                <CampaignScheduleSummary
                  items={form.configuration.schedule.scheduleItems}
                  noSchedule={form.configuration.schedule.noSchedule}
                />
              </CampaignSummaryItem>
              <CampaignSummaryItem
                id="summary-weather"
                title={translate("label.preferred.weather")}
                sxChildren={{ display: "flex", flexWrap: "wrap", gap: "1rem", mt: ".6rem" }}>
                <CampaignWeatherSummary weathers={form.configuration.schedule.weathers} />
              </CampaignSummaryItem>
              {typeof form.configuration.schedule.min_celsius === "number" && (
                <CampaignSummaryItem
                  id="summary-temperature-under"
                  title={`${translate("label.temperature.under")} (${getTemperatureUnitSymbol()})`}
                  value={getConvertedTemperature(form.configuration.schedule.min_celsius)}
                />
              )}
              {typeof form.configuration.schedule.max_celsius === "number" && (
                <CampaignSummaryItem
                  id="summary-temperature-above"
                  title={`${translate("label.temperature.above")} (${getTemperatureUnitSymbol()})`}
                  value={getConvertedTemperature(form.configuration.schedule.max_celsius)}
                />
              )}
            </CampaignSummaryColumn>
          </CampaignSummaryItemLayout>
          <CampaignSummaryItemLayout
            // ****************** GEO-TARGETS ******************
            id="geo-targets"
            title={translate("campaign.geo-targets")}
            isEditable={isEditable}
            onEdit={() => editFormFromSummaryView(FORM_TABS.GEO_TARGETS, isForecast, isDspDeal)}
            xl={12}
            md={12}>
            <CampaignSummaryColumn contentWidth={cw} sx={{ p: 0 }}>
              <CampaignGeoTargetsSummary geoTargets={form.geoTargets} contentWidth={cw} />
            </CampaignSummaryColumn>
          </CampaignSummaryItemLayout>
          <CampaignSummaryItemLayout
            // ****************** SCREEN - ENVIRONMENTS ******************
            id="screen"
            title={translate("environments.title")}
            isEditable={isEditable}
            onEdit={() =>
              editFormFromSummaryView(FORM_TABS.SCREENS, isForecast, isDspDeal, FORM_TAB_BOXES.SCREENS_ENVIRONMENTS)
            }
            xl={6}
            md={6}>
            <CampaignSummaryColumn contentWidth={cw}>
              <CampaignSummaryItem
                id="summary-environment"
                title={translate("label.included")}
                sxChildren={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "start",
                  gap: "1rem",
                  ml: "1rem",
                  mt: ".5rem",
                  maxHeight: "10rem",
                  overflow: "auto"
                }}>
                <CampaignEnvironmentsSummary environmentIds={form.screens.environmentsIds} />
              </CampaignSummaryItem>
            </CampaignSummaryColumn>
          </CampaignSummaryItemLayout>
          <CampaignSummaryItemLayout
            // ****************** SCREEN - SSP ******************
            id="screen-ssp"
            title={translate("ssp.title")}
            isEditable={isEditable}
            onEdit={() => editFormFromSummaryView(FORM_TABS.SCREENS, isForecast, isDspDeal, FORM_TAB_BOXES.SCREENS_SSP)}
            xl={6}
            md={6}>
            <CampaignSummaryColumn contentWidth={cw}>
              <CampaignSummaryItem
                id="summary-ssp"
                title={translate("ssp.active")}
                sxChildren={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "start",
                  gap: "1rem",
                  ml: "1rem",
                  mt: ".5rem",
                  maxHeight: "10rem",
                  overflow: "auto"
                }}>
                <CampaignSspSummary sspIds={form.screens.sspIds} />
              </CampaignSummaryItem>
            </CampaignSummaryColumn>
          </CampaignSummaryItemLayout>
          {!isDspDeal && (
            <CampaignSummaryItemLayout
              // ****************** SCREEN - SSP DEALS ******************
              id="screen-dsp-deals"
              title={translate("dsp-deals.title")}
              isEditable={isEditable}
              onEdit={() =>
                editFormFromSummaryView(FORM_TABS.SCREENS, isForecast, isDspDeal, FORM_TAB_BOXES.SCREENS_SSP_DEALS)
              }
              xl={6}
              md={6}>
              <CampaignSummaryColumn contentWidth={cw}>
                {form.configuration.main.isBiddingOnlyOnDeals && (
                  <CampaignSummaryItem
                    id="summary-ssp-deal-bid-only-on-deal"
                    title={translate("label.bids")}
                    value={translate("campaign.ssp-deal.bid.only.on.deal")}
                  />
                )}
                <CampaignSummaryItem
                  id="summary-ssp-deals"
                  title={translate("label.active.dsp-deals")}
                  sxChildren={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "start",
                    gap: "1rem",
                    ml: "1rem",
                    mt: ".5rem",
                    maxHeight: "10rem",
                    overflow: "auto"
                  }}>
                  <CampaignSspDealsSummary campaignSspDeals={form.screens.campaignSspDeals} />
                </CampaignSummaryItem>
              </CampaignSummaryColumn>
            </CampaignSummaryItemLayout>
          )}
          <CampaignSummaryItemLayout
            // ****************** SCREEN - OPERATOR ******************
            id="screen-operator"
            title={translate("operators.title")}
            isEditable={isEditable}
            onEdit={() => editFormFromSummaryView(FORM_TABS.SCREENS, isForecast, isDspDeal, FORM_TAB_BOXES.SCREENS_SSP)}
            xl={isDspDeal ? 12 : 6}
            md={isDspDeal ? 12 : 6}>
            <CampaignSummaryColumn contentWidth={cw}>
              <CampaignSummaryItem
                id="summary-operators"
                title={translate("label.included")}
                sxChildren={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "start",
                  gap: "1rem",
                  ml: "1rem",
                  mt: ".5rem",
                  maxHeight: "10rem",
                  overflow: "auto"
                }}>
                <CampaignOperatorsSummary operators={getSummaryOperatorsWithUmbracityIfNeeded(form)} />
              </CampaignSummaryItem>
            </CampaignSummaryColumn>
          </CampaignSummaryItemLayout>
          <CampaignSummaryItemLayout
            // ****************** CREATIVES ******************
            id="creatives"
            title={translate(
              isForecast ? "media.creatives.required" : isDspDeal ? "label.media.formats" : "media.creatives"
            )}
            isEditable={isEditable}
            onEdit={() => editFormFromSummaryView(FORM_TABS.CREATIVES, isForecast, isDspDeal)}
            xl={12}
            md={12}>
            {isForecast ? (
              <CampaignSummaryColumn contentWidth={cw} sx={{ p: 0 }}>
                <ForecastRequiredCreativeSummary contentWidth={cw} form={form} />
              </CampaignSummaryColumn>
            ) : isDspDeal ? (
              <CampaignSummaryColumn contentWidth={cw} sx={{ p: 0 }}>
                <DspDealFormatSummary formatAssociations={form.context.relations.dspDealFormats} contentWidth={cw} />
              </CampaignSummaryColumn>
            ) : (
              <CampaignSummaryColumn contentWidth={cw} sx={{ p: 0 }}>
                <CampaignCreativeSummary medias={form.creatives.medias} contentWidth={cw} />
              </CampaignSummaryColumn>
            )}
          </CampaignSummaryItemLayout>
        </Grid>
      </FlexBox>
    )
  }
)
